import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { chartClasses } from "../utils/chartUtils";
import { useTheme } from "@mui/material";

export const useChart = (
  ref: RefObject<SVGSVGElement>,
  legend: {
    focused?: null | string;
    hidden?: null | string[];
  },
  setLegend: Dispatch<
    SetStateAction<{
      hidden?: null | string[];
    }>
  >
) => {
  const theme = useTheme();

  const axisProps = {
    x: {
      disableLine: true,
      disableTicks: true,
      tickLabelStyle: {
        fill: theme.palette.text.secondary,
        transform: "translateY(-15px)",
      },
    },
    y: {
      disableLine: true,
      disableTicks: true,
      tickLabelStyle: {
        fill: theme.palette.text.secondary,
      },
    },
  };

  const pathsRef = useRef<NodeListOf<SVGPathElement> | null>(null);
  const markersRef = useRef<NodeListOf<SVGPathElement> | null>(null);
  const svg = ref.current;

  const highlightSerie = (id: string | undefined | null) => {
    const paths = pathsRef.current;
    const markers = markersRef.current;
    if (!paths || !markers) return;

    if (!id) {
      [...paths, ...markers].forEach((item) => {
        item.classList.remove(chartClasses.highlighted);
        item.classList.remove(chartClasses.dimmed);
      });
    } else {
      paths.forEach((path) => {
        path.classList.add(chartClasses.dimmed);
        path.classList.remove(chartClasses.highlighted);
      });

      [...paths, ...markers].forEach((item) => {
        if (
          item.classList.contains(`MuiLineElement-series-${id}`) ||
          item.classList.contains(`MuiMarkElement-series-${id}`)
        ) {
          item.classList.add(chartClasses.highlighted);
          item.classList.remove(chartClasses.dimmed);
        } else {
          item.classList.add(chartClasses.dimmed);
          item.classList.remove(chartClasses.highlighted);
        }
      });
    }
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const svg = ref.current;
      const paths = pathsRef.current;
      const markers = markersRef.current;
      if (!svg || !paths || !markers) return;

      let serieId: string | undefined = undefined;

      const createSVGPoint = (x: number, y: number) => {
        const point = svg.createSVGPoint();
        point.x = x;
        point.y = y;
        return point;
      };

      const point = createSVGPoint(event.clientX, event.clientY);
      const transformedPoint = point.matrixTransform(
        svg.getScreenCTM()?.inverse()
      );

      const createGridPoints = (
        cx: number,
        cy: number,
        offset: number,
        step: number
      ) => {
        const points = [];
        for (let x = cx - offset; x <= cx + offset; x += step) {
          for (let y = cy - offset; y <= cy + offset; y += step) {
            points.push(createSVGPoint(x, y));
          }
        }
        return points;
      };

      const offset = 4;
      const step = 2;

      const pointsToCheck = createGridPoints(
        transformedPoint.x,
        transformedPoint.y,
        offset,
        step
      );

      paths.forEach((path) => {
        const isInPath = pointsToCheck.some((point) =>
          path.isPointInStroke(point)
        );

        if (isInPath) {
          const className = [...(path as SVGPathElement).classList].find(
            (cls) => cls.includes("MuiLineElement-series")
          );
          serieId = className?.split("-").pop();
        }
      });

      if (serieId) {
        setLegend((s) => ({ ...s, focused: serieId }));
      } else {
        setLegend((s) => ({ ...s, focused: undefined }));
      }
    },
    [setLegend, ref]
  );

  useEffect(() => {
    if (!svg) return;
    pathsRef.current = svg.querySelectorAll<SVGPathElement>(
      "path.MuiLineElement-root"
    );
    markersRef.current = svg.querySelectorAll<SVGPathElement>(
      "path.MuiMarkElement-root"
    );
    if (svg) svg.addEventListener("mousemove", handleMouseMove);
    return () => {
      if (svg) svg.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMouseMove, legend.focused, svg]);

  useEffect(() => {
    highlightSerie(legend.focused);
  });
  return { axisProps };
};
